'use client';

import type { IconName } from '@/components/entities/Sprite/Icon';
import type { CategoryListItemDto } from '@/queries/categories/categories.schema';
import { useQuery } from '@tanstack/react-query';
import { useLocale } from 'next-intl';
import { findCampaignCategory } from '@/libs/campaigns/category';
import { localizeMessages } from '@/libs/campaigns/messages';
import { queryActiveCategoryByAlias } from '@/queries/categories/categories.queries';
import { useActiveSkin } from '../ActiveCampaignProvider';

export type CampaignCategorySpot = {
  /** The backend category: its id, games and covers. */
  category: CategoryListItemDto;
  icon: IconName;
  sidebarLabel: string;
  lobbyTitle: string;
};

/**
 * The Active Campaign's category for the sidebar's Campaign spot and the lobby's first row,
 * or `null` without an Active Campaign or while the backend has no active category with its alias.
 */
export function useCampaignCategory(): CampaignCategorySpot | null {
  const campaignCategory = useActiveSkin()?.category;
  const locale = useLocale();
  const alias = campaignCategory?.alias ?? '';
  const { data } = useQuery({ ...queryActiveCategoryByAlias(alias), enabled: Boolean(campaignCategory) });

  const category = findCampaignCategory(data?.items ?? [], alias);

  if (!campaignCategory || !category) {
    return null;
  }

  return {
    category,
    icon: campaignCategory.icon,
    ...localizeMessages(campaignCategory.messages, locale),
  };
}

/** The categories without the one in the Campaign spot, so it is shown once. */
export function withoutCampaignCategory<T extends { alias: string }>(
  categories: T[],
  spot: CampaignCategorySpot | null,
): T[] {
  return spot ? categories.filter(category => category.alias !== spot.category.alias) : categories;
}
